import React, { Component, ErrorInfo } from 'react'
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons'
import { streamApplicationError } from '../../_utils/airbrake'
import { Container } from 'typescript-ioc/es5'
import { AppState } from '../../stores/app'
import { MobileEmptyState } from './mobile-empty-state'

interface Props {
    children: React.ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
}

/**
 * Mobile-specific error boundary. Catches render errors in the mobile view tree
 * and shows a recovery UI instead of crashing the entire app.
 *
 * Reports errors to Airbrake for observability.
 */
export class MobileErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false, error: null }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        try {
            const appState = Container.get(AppState)
            streamApplicationError(error, info, appState.currentUser, appState.currentDomain)
        } catch {
            // Error reporting failure should not compound the original error
        }
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null })
    }

    render() {
        if (this.state.hasError) {
            return (
                <MobileEmptyState
                    icon={<WarningOutlined className="mobile-empty-state__icon--error" />}
                    description="Something went wrong"
                    message="An unexpected error occurred. Try again or return to the dashboard."
                    action={{ label: 'Try Again', onClick: this.handleRetry, icon: <ReloadOutlined /> }}
                    fullPage
                />
            )
        }

        return this.props.children
    }
}
